/**
 * Background Image URL Extractor
 * 
 * Utility to extract image URLs from CSS background-image properties.
 * Handles various url() formats and resolves relative URLs.
 */

/**
 * Regular expression to extract URL from background-image value.
 * Matches: url('path'), url("path"), url(path)
 */
const BACKGROUND_IMAGE_URL_REGEX = /url\(\s*(['"]?)([^'"()]+)\1\s*\)/i;

/**
 * Extract the image URL from an element's computed background-image CSS property.
 * 
 * @param element - The HTML element to extract background-image from
 * @returns The absolute URL of the background image, or null if not found
 * 
 * @example
 * // Element with inline style: style="background-image: url('hero.jpg')"
 * extractBackgroundImageUrl(element) // => 'https://example.com/hero.jpg'
 * 
 * // Element with CSS class setting background-image
 * extractBackgroundImageUrl(element) // => 'https://example.com/bg.png'
 * 
 * // Element with no background-image or gradient
 * extractBackgroundImageUrl(element) // => null
 */
export function extractBackgroundImageUrl(element: HTMLElement): string | null {
    // Get computed style to handle inline styles, CSS classes, and CSS variables
    const computedStyle = window.getComputedStyle(element);
    const backgroundImage = computedStyle.backgroundImage;

    // Check if there's no background image or it's explicitly 'none'
    if (!backgroundImage || backgroundImage === 'none') {
        return null;
    }

    // Handle multiple background images - use the first one
    // Multiple backgrounds are comma-separated, e.g., "url(...), url(...)"
    const firstBackground = backgroundImage.split(',')[0].trim();

    // Skip gradients (linear-gradient, radial-gradient, conic-gradient, etc.)
    if (firstBackground.includes('gradient(')) {
        return null;
    }

    // Extract URL from url(...) syntax
    const match = firstBackground.match(BACKGROUND_IMAGE_URL_REGEX);
    if (!match || !match[2]) {
        return null;
    }

    const extractedUrl = match[2].trim();

    // Skip data URLs (base64 images, SVGs, etc.)
    if (extractedUrl.startsWith('data:')) {
        return null;
    }

    // Resolve relative URLs to absolute using the document's base URI
    try {
        return new URL(extractedUrl, document.baseURI).href;
    } catch {
        // If URL parsing fails, return the original (likely already absolute)
        return extractedUrl;
    }
}

/**
 * Get the image URL from an element, checking both src attribute and background-image.
 * 
 * @param element - The HTML element to get image URL from
 * @returns Object with the image URL and source type, or null if no image found
 */
export function getImageUrl(element: HTMLElement): { url: string; sourceType: 'img' | 'background-image' } | null {
    // Check if it's an img element with src attribute
    if (element instanceof HTMLImageElement && element.src) {
        return {
            url: element.src,
            sourceType: 'img',
        };
    }

    // Check for background-image
    const backgroundUrl = extractBackgroundImageUrl(element);
    if (backgroundUrl) {
        return {
            url: backgroundUrl,
            sourceType: 'background-image',
        };
    }

    return null;
}

/**
 * Check if an element has an animatable image (either img src or background-image)
 * 
 * @param element - The HTML element to check
 * @returns true if the element has an image that can be animated
 */
export function hasAnimatableImage(element: HTMLElement): boolean {
    return getImageUrl(element) !== null;
}
